/**
 * Settings (route /settings/*, Direction A — round9/design-pass): quoting
 * toggle, rate card and engineering-rules self-service (PUT
 * /api/portal/tenant via updateTenant() in lib/api.ts), behind a left rail
 * that switches which SECTION of this one form is on screen:
 *   /settings/rate-card   Rate card & pricing (rate card, modifiers,
 *                         pricing extensions, estimate pricing)
 *   /settings/quoting     Quoting
 *   /settings/engineering Engineering rules
 * The rail's fourth item, Embed, is its own page (EmbedPage.tsx) with its
 * own Save. /rate-card redirects here (main.tsx). Templates, formerly a card
 * at the foot of this page, is now the top-level /templates (TemplatesPage).
 *
 * One page, one Save: the backend is a single PUT accepting up to nine
 * top-level keys, and per-section save would add independent loading/
 * error/dirty states for no backend benefit — see the implementation plan
 * for the full reasoning. The rail does not change that: only the active
 * section RENDERS, but every section's draft lives in this component's
 * state, this component stays mounted across /settings/* navigation (one
 * <Route path="/settings/*">), and Save sends the whole patch. See
 * components/shell/settingsSections.ts.
 *
 * `designerPreset` (Round 7 D3 — the "Embed configuration" section below)
 * and `engineeringRules` (round8/engineering-rules — the "Engineering
 * rules" section right after it) are two of those nine keys: both plain
 * single-value-shaped fields, folded into this same one-Save form rather
 * than given an independent list/delete-style action area (contrast the
 * Templates page, /templates, which IS independent — see TemplatesPage.tsx).
 * Two deliberate choices worth flagging up front,
 * introduced for `designerPreset` and now shared by `engineeringRules`:
 *   - OWNER-ONLY UI: unlike every other field on this page (which any
 *     signed-in user sees, relying entirely on the backend's existing
 *     403 FORBIDDEN to stop a `rep` from actually saving), this section
 *     reads `user.role` and shows a `rep` a read-only summary instead of
 *     the picker. This is now possible because auth.ts decodes the
 *     Cognito ID token's `custom:role` claim (round7/d3-scope-config-
 *     portal — see DEBT.md's round4/builder-templates-portal entry,
 *     which flagged this exact claim as newly available and unconsumed).
 *     round7/debt-sweep closed the rest of that same DEBT.md entry: each
 *     Templates row's "Delete" button (now on TemplatesPage.tsx) is the
 *     second consumer of `user.role`, hidden outright for a `rep` the same
 *     way. round8/engineering-rules' "Engineering rules" section is the
 *     third consumer, same pattern again. The rest of this page's fields
 *     are still NOT retrofitted to use it — these three sections are the
 *     only consumers, kept narrowly scoped to what each task actually
 *     needed.
 *   - NO CLIENT-SIDE TIER GATE: a preset a tenant's tier doesn't allow is
 *     never disabled/hidden here — see the "Embed configuration" section
 *     below for why reproducing `isPresetAllowedForTier` client-side would
 *     be actively wrong, not just duplicated, and how the resulting 422
 *     is surfaced instead. (`engineeringRules` has no tier-gating analog —
 *     its only server-side validation is the bounds check below.)
 *
 * rateCard / rateCardModifiers / pricingExtensions are whole-object
 * REPLACEs server-side, so this page always starts from the tenant's full
 * current object (via getTenant on mount) and layers edits on top before
 * sending it back.
 *
 * rateCard.stairFlatUSD (per-stair-style pricing, a dynamic map) is
 * deliberately NOT edited here — this page only manages the fixed scalar
 * fields. Whatever the tenant already has in stairFlatUSD is preserved
 * untouched via passthrough (preservedStairFlatUSD below); without this,
 * saving the page would silently delete any existing per-style pricing.
 * THE LESSON: never build a "whole object replace" patch from only the
 * fields a form happens to expose — either send everything (as done here)
 * or, if the backend supports it, prefer a merge (as `pricing` does below).
 *
 * `pricing` (TenantPricing — deckingRatesPerSqft / featureAddons /
 * laborApproach / laborValue / marginPct) is the ONE field group below
 * that is NOT a whole-object replace: the backend merges it server-side,
 * so this page only ever sends the sub-keys that actually have content
 * (mirroring how rateCardModifiers/pricingExtensions already send only
 * their filled-in fields, just one level up — at the top-level-key
 * granularity instead of the whole `pricing` object). This is the object
 * that actually drives the estimated range shown on a lead and in the
 * customer PDF (see lib/estimate.ts) — rateCard/pricingExtensions above
 * feed a different, separate quoting path.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { MATERIAL_PRESETS } from "@dockpro/core/catalogs";
import { useAuth } from "../contexts/auth-context";
import {
  getTenant, updateTenant, ApiError,
  type TenantConfig, type UpdateTenantPatch, type TenantPricing,
} from "../lib/api";
import { StatusMessage } from "../components/StatusMessage";
import { friendlyErrorMessage } from "../components/friendlyErrorMessage";
import { SettingsLayout } from "../components/shell/SettingsRail";
import { settingsSection, settingsSectionFromPath } from "../components/shell/settingsSections";
import ui from "../styles/ui.module.css";
import styles from "./SettingsPage.module.css";

const MATERIAL_OPTIONS = Object.entries(MATERIAL_PRESETS).map(
  ([key, preset]) => ({ key, label: (preset as { label: string }).label }),
);

// TenantPricing.deckingRatesPerSqft keys are a catalog material OR the
// special "default" fallback (see estimate.ts's 3-tier fallback:
// rates[material] ?? rates["default"] ?? DEFAULT_DECK_RATE_PER_SQFT) — not
// a catalog key itself, so it's added on top of MATERIAL_OPTIONS here.
const DECKING_RATE_MATERIAL_OPTIONS = [
  { key: "default", label: "Default (fallback rate)" },
  ...MATERIAL_OPTIONS,
];

type QuoteVisibility = "homeowner" | "internal";
type MarginMode = "onCost" | "onPrice";

interface RateCardDraft {
  perDeckSqFtPierUSD: string;
  perDeckSqFtFloatingUSD: string;
  perSundeckSqFtUSD: string;
  perRoofSqFtUSD: string;
  perRailingLFUSD: string;
  perGangwayFlatUSD: string;
  perSlipAlongsideUSD: string;
  perSlipPullinUSD: string;
  stairFlatFallbackUSD: string;
  baseFeeUSD: string;
}

const EMPTY_RATE_CARD_DRAFT: RateCardDraft = {
  perDeckSqFtPierUSD: "", perDeckSqFtFloatingUSD: "", perSundeckSqFtUSD: "",
  perRoofSqFtUSD: "", perRailingLFUSD: "", perGangwayFlatUSD: "",
  perSlipAlongsideUSD: "", perSlipPullinUSD: "", stairFlatFallbackUSD: "", baseFeeUSD: "",
};

const RATE_CARD_FIELDS: Array<{ key: keyof RateCardDraft; label: string }> = [
  { key: "perDeckSqFtPierUSD", label: "Deck, pier ($/sqft)" },
  { key: "perDeckSqFtFloatingUSD", label: "Deck, floating ($/sqft)" },
  { key: "perSundeckSqFtUSD", label: "Sundeck ($/sqft)" },
  { key: "perRoofSqFtUSD", label: "Roof ($/sqft)" },
  { key: "perRailingLFUSD", label: "Railing ($/LF)" },
  { key: "perGangwayFlatUSD", label: "Gangway, flat ($)" },
  { key: "perSlipAlongsideUSD", label: "Slip, alongside ($)" },
  { key: "perSlipPullinUSD", label: "Slip, pull-in ($)" },
  { key: "stairFlatFallbackUSD", label: "Stair, fallback ($)" },
  { key: "baseFeeUSD", label: "Base fee ($)" },
];

interface ModifiersDraft {
  regionalMultiplier: string;
  marginPct: string;
  rangeSpreadPct: string;
}
const EMPTY_MODIFIERS_DRAFT: ModifiersDraft = { regionalMultiplier: "", marginPct: "", rangeSpreadPct: "" };

interface ExtensionsDraft {
  perGangwayLFUSD: string;
  marginMode: MarginMode;
  perFloatUSD: string;
}
const EMPTY_EXTENSIONS_DRAFT: ExtensionsDraft = { perGangwayLFUSD: "", marginMode: "onCost", perFloatUSD: "" };

interface DeckMaterialRateRow {
  id: number;
  material: string;
  rate: string;
}

// TenantPricing.featureAddons — exactly these seven keys per the backend
// contract; each independently optional, like rateCardModifiers above.
interface FeatureAddonsDraft {
  pullinSlip: string;
  sideTieSlip: string;
  boatLift: string;
  stairStraight: string;
  stairL: string;
  roof: string;
  railingPerFt: string;
}
const EMPTY_FEATURE_ADDONS_DRAFT: FeatureAddonsDraft = {
  pullinSlip: "", sideTieSlip: "", boatLift: "",
  stairStraight: "", stairL: "", roof: "", railingPerFt: "",
};

const FEATURE_ADDON_FIELDS: Array<{ key: keyof FeatureAddonsDraft; label: string }> = [
  { key: "pullinSlip", label: "Pull-in slip ($)" },
  { key: "sideTieSlip", label: "Side-tie slip ($)" },
  { key: "boatLift", label: "Boat lift ($)" },
  { key: "stairStraight", label: "Stair, straight ($)" },
  { key: "stairL", label: "Stair, L-shape ($)" },
  { key: "roof", label: "Roof ($)" },
  { key: "railingPerFt", label: "Railing ($/ft)" },
];

type LaborApproach = NonNullable<TenantPricing["laborApproach"]>;
const LABOR_APPROACH_OPTIONS: Array<{ value: LaborApproach; label: string }> = [
  { value: "built-in", label: "Built in (no separate labor line)" },
  { value: "markup-pct", label: "Markup % on total" },
  { value: "hourly", label: "Hourly rate" },
  { value: "fixed-min", label: "Fixed minimum" },
];

// round9/embed-config: the designer-preset picker (DESIGNER_PRESET_OPTIONS,
// its draft, its PUT key and its two error codes) moved to EmbedPage.tsx /
// lib/embedConfig.ts, together with the new per-tool / per-option / custom
// slip-size configuration. The "Embed configuration" card below is now a
// pointer. Older comments in this file that describe the picker's history on
// this page are kept as history.

// Engineering rules (v1: pilingSpacingFt only) — round8/engineering-rules.
// Bounds/default mirror @dockpro/core's ENGINEERING_RULES.pilingSpacingFt
// registry entry (6-12 ft, default 8 ft — see
// docs/planning/DockPro-Engineering-Rules-Plan.md in the sibling DockPro
// repo for the full design). This repo has no dependency on that registry
// at runtime (it isn't part of @dockpro/core's public export surface this
// repo imports from), so these are hardcoded UI constants for a courtesy
// client-side pre-check ONLY — exactly the same posture as rangeSpreadPct's
// 0-1 bound above. The backend's real resolveEngineeringRules() is the
// actual enforcement (400 INVALID_ENGINEERING_RULES on an out-of-bounds
// value); nothing here needs to match it exactly, just stay in sync well
// enough to catch the common case before a round-trip.
const PILING_SPACING_MIN_FT = 6;
const PILING_SPACING_MAX_FT = 12;
const PILING_SPACING_DEFAULT_FT = 8;

function isPositiveFiniteStr(s: string): boolean {
  if (s.trim() === "") return false;
  const n = Number(s);
  return Number.isFinite(n) && n > 0;
}

export function SettingsPage() {
  const { user, signOut } = useAuth();

  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [quotingEnabled, setQuotingEnabled] = useState(false);
  const [quoteVisibility, setQuoteVisibility] = useState<QuoteVisibility | "">("");
  const [disclaimerText, setDisclaimerText] = useState("");

  // Engineering rules (round8/engineering-rules). Unlike designerPreset
  // above, this ALWAYS holds a concrete value — the tenant's override once
  // loaded, or PILING_SPACING_DEFAULT_FT before load / when the tenant has
  // none — never a "not set" sentinel, since the field always has a real
  // effective value the owner is editing in place (see the constants' own
  // doc comment). Initialized to the default here so the owner view has a
  // sane value even in the unreachable instant before the mount effect's
  // getTenant() resolves.
  const [pilingSpacingDraft, setPilingSpacingDraft] = useState(String(PILING_SPACING_DEFAULT_FT));

  const [rateCardDraft, setRateCardDraft] = useState<RateCardDraft>(EMPTY_RATE_CARD_DRAFT);
  const [preservedStairFlatUSD, setPreservedStairFlatUSD] =
    useState<Record<string, number> | undefined>(undefined);

  const [modifiersDraft, setModifiersDraft] = useState<ModifiersDraft>(EMPTY_MODIFIERS_DRAFT);

  const [extensionsDraft, setExtensionsDraft] = useState<ExtensionsDraft>(EMPTY_EXTENSIONS_DRAFT);
  const [loadedMarginMode, setLoadedMarginMode] = useState<MarginMode>("onCost");
  const [hadPricingExtensions, setHadPricingExtensions] = useState(false);

  const [deckMaterialRows, setDeckMaterialRows] = useState<DeckMaterialRateRow[]>([]);
  const nextRowId = useRef(0);

  // Estimate pricing (TenantPricing — drives lib/estimate.ts, not the rate
  // card above). Backend merges this key-by-key, so every field here is
  // independently optional and sent only when filled in.
  const [deckingRateRows, setDeckingRateRows] = useState<DeckMaterialRateRow[]>([]);
  const [featureAddonsDraft, setFeatureAddonsDraft] =
    useState<FeatureAddonsDraft>(EMPTY_FEATURE_ADDONS_DRAFT);
  const [laborApproachDraft, setLaborApproachDraft] = useState<LaborApproach | "">("");
  const [laborValueDraft, setLaborValueDraft] = useState("");
  const [pricingMarginPctDraft, setPricingMarginPctDraft] = useState("");

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // round9/design-pass: which rail section is on screen. Read from the URL
  // (/settings/<section>) so a section is linkable; unknown → rate-card.
  const location = useLocation();
  const section = settingsSectionFromPath(location.pathname);
  const sectionMeta = settingsSection(section);

  // Populates every draft field from a freshly-fetched TenantConfig. Shared
  // by the mount effect below AND handleRetryLoad (AUD-008's in-place
  // Retry) so a successful retry repopulates the WHOLE form — not just the
  // three top-level fields — exactly like the initial load does.
  const applyTenantToForm = (result: TenantConfig) => {
    setTenant(result);
    setQuotingEnabled(result.quotingEnabled ?? false);
    setQuoteVisibility(result.quoteVisibility ?? "");
    setDisclaimerText(result.disclaimerText ?? "");
    // Engineering rules: always the CURRENT EFFECTIVE value — the tenant's
    // override if set, else the hardcoded default — never blank (see this
    // draft's own doc comment above for why it differs from designerPreset's
    // "" convention just above).
    setPilingSpacingDraft(
      String(result.engineeringRules?.pilingSpacingFt ?? PILING_SPACING_DEFAULT_FT),
    );

    if (result.rateCard) {
      const rc = result.rateCard;
      setRateCardDraft({
        perDeckSqFtPierUSD: String(rc.perDeckSqFtPierUSD),
        perDeckSqFtFloatingUSD: String(rc.perDeckSqFtFloatingUSD),
        perSundeckSqFtUSD: String(rc.perSundeckSqFtUSD),
        perRoofSqFtUSD: String(rc.perRoofSqFtUSD),
        perRailingLFUSD: String(rc.perRailingLFUSD),
        perGangwayFlatUSD: String(rc.perGangwayFlatUSD),
        perSlipAlongsideUSD: String(rc.perSlipFlatUSD.alongside),
        perSlipPullinUSD: String(rc.perSlipFlatUSD.pullin),
        stairFlatFallbackUSD: String(rc.stairFlatFallbackUSD),
        baseFeeUSD: String(rc.baseFeeUSD),
      });
      setPreservedStairFlatUSD(rc.stairFlatUSD);
    }

    if (result.rateCardModifiers) {
      const m = result.rateCardModifiers;
      setModifiersDraft({
        regionalMultiplier: m.regionalMultiplier !== undefined ? String(m.regionalMultiplier) : "",
        marginPct: m.marginPct !== undefined ? String(m.marginPct) : "",
        rangeSpreadPct: m.rangeSpreadPct !== undefined ? String(m.rangeSpreadPct) : "",
      });
    }

    if (result.pricingExtensions) {
      const p = result.pricingExtensions;
      setExtensionsDraft({
        perGangwayLFUSD: p.perGangwayLFUSD !== undefined ? String(p.perGangwayLFUSD) : "",
        marginMode: p.marginMode ?? "onCost",
        perFloatUSD: p.perFloatUSD !== undefined ? String(p.perFloatUSD) : "",
      });
      setLoadedMarginMode(p.marginMode ?? "onCost");
      setHadPricingExtensions(true);
      if (p.deckRatesByMaterialUSD) {
        setDeckMaterialRows(
          Object.entries(p.deckRatesByMaterialUSD).map(([material, rate]) => ({
            id: nextRowId.current++,
            material,
            rate: String(rate),
          })),
        );
      }
    }

    if (result.pricing) {
      const pr = result.pricing;
      if (pr.deckingRatesPerSqft) {
        setDeckingRateRows(
          Object.entries(pr.deckingRatesPerSqft).map(([material, rate]) => ({
            id: nextRowId.current++,
            material,
            rate: String(rate),
          })),
        );
      }
      if (pr.featureAddons) {
        const fa = pr.featureAddons;
        setFeatureAddonsDraft({
          pullinSlip: fa.pullinSlip !== undefined ? String(fa.pullinSlip) : "",
          sideTieSlip: fa.sideTieSlip !== undefined ? String(fa.sideTieSlip) : "",
          boatLift: fa.boatLift !== undefined ? String(fa.boatLift) : "",
          stairStraight: fa.stairStraight !== undefined ? String(fa.stairStraight) : "",
          stairL: fa.stairL !== undefined ? String(fa.stairL) : "",
          roof: fa.roof !== undefined ? String(fa.roof) : "",
          railingPerFt: fa.railingPerFt !== undefined ? String(fa.railingPerFt) : "",
        });
      }
      setLaborApproachDraft(pr.laborApproach ?? "");
      setLaborValueDraft(pr.laborValue !== undefined ? String(pr.laborValue) : "");
      setPricingMarginPctDraft(pr.marginPct !== undefined ? String(pr.marginPct) : "");
    }
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getTenant(user.idToken)
      .then((result) => {
        if (cancelled) return;
        applyTenantToForm(result);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setLoadError(friendlyErrorMessage(err, "loading your rate card"));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, signOut]);

  // AUD-008 — a manual, in-place retry for the load-failure branch below
  // (previously the only recovery was "Back to leads"). Separate from the
  // mount effect above for the same reason LeadDetailPage's handleRetryLoad
  // is: user-triggered, so it doesn't need the effect's unmount `cancelled`
  // guard. Reuses applyTenantToForm so a successful retry repopulates the
  // whole form, not just the top-level fields.
  const handleRetryLoad = useCallback(() => {
    if (!user) return;
    setLoadError(null);
    setIsLoading(true);
    getTenant(user.idToken)
      .then((result) => {
        applyTenantToForm(result);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setLoadError(friendlyErrorMessage(err, "loading your rate card"));
      })
      .finally(() => setIsLoading(false));
  }, [user, signOut]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaveError(null);
    setSaved(false);
    if (!user) return;

    const patch: UpdateTenantPatch = { quotingEnabled };
    if (quoteVisibility) patch.quoteVisibility = quoteVisibility;
    if (disclaimerText.trim()) patch.disclaimerText = disclaimerText.trim();
    // Engineering rules (v1: pilingSpacingFt only). Unlike the optional
    // modifiers below (blank means "leave unchanged"), this draft always
    // holds a concrete value — like quotingEnabled above, it's always
    // included in the patch once validated, not gated behind a
    // ".trim() !== ''" check. Courtesy client-side pre-check only, mirroring
    // rangeSpreadPct's own bounds check below — see PILING_SPACING_MIN_FT's
    // doc comment for why this repo can't just import the real bounds, and
    // the backend's resolveEngineeringRules() is what actually enforces
    // them (400 INVALID_ENGINEERING_RULES).
    const pilingSpacingFtN = Number(pilingSpacingDraft);
    if (
      !Number.isFinite(pilingSpacingFtN) ||
      pilingSpacingFtN < PILING_SPACING_MIN_FT ||
      pilingSpacingFtN > PILING_SPACING_MAX_FT
    ) {
      setSaveError(
        `Piling spacing must be a number between ${PILING_SPACING_MIN_FT} and ${PILING_SPACING_MAX_FT} ft.`,
      );
      return;
    }
    patch.engineeringRules = { pilingSpacingFt: pilingSpacingFtN };

    // Rate card: all-or-nothing, matching validateRateCardConfig server-side
    // (rateCard is a whole-object replace, so a partial object would either
    // fail validation or silently drop fields the tenant already had).
    const rcEntries = Object.entries(rateCardDraft);
    const rcFilled = rcEntries.filter(([, v]) => v.trim() !== "");
    if (rcFilled.length > 0) {
      if (rcFilled.length < rcEntries.length) {
        setSaveError("Complete all Rate Card fields before saving — each price must be greater than 0.");
        return;
      }
      if (rcEntries.some(([, v]) => !isPositiveFiniteStr(v))) {
        setSaveError("Every Rate Card price must be a number greater than 0.");
        return;
      }
      patch.rateCard = {
        perDeckSqFtPierUSD: Number(rateCardDraft.perDeckSqFtPierUSD),
        perDeckSqFtFloatingUSD: Number(rateCardDraft.perDeckSqFtFloatingUSD),
        perSundeckSqFtUSD: Number(rateCardDraft.perSundeckSqFtUSD),
        perRoofSqFtUSD: Number(rateCardDraft.perRoofSqFtUSD),
        perRailingLFUSD: Number(rateCardDraft.perRailingLFUSD),
        perGangwayFlatUSD: Number(rateCardDraft.perGangwayFlatUSD),
        perSlipFlatUSD: {
          alongside: Number(rateCardDraft.perSlipAlongsideUSD),
          pullin: Number(rateCardDraft.perSlipPullinUSD),
        },
        stairFlatFallbackUSD: Number(rateCardDraft.stairFlatFallbackUSD),
        baseFeeUSD: Number(rateCardDraft.baseFeeUSD),
        // Preserved untouched — see the file doc comment. RateCard.stairFlatUSD
        // is non-optional on the core type, so default to {} when there's
        // nothing to preserve rather than omitting the field.
        stairFlatUSD: preservedStairFlatUSD ?? {},
      };
    }

    // Rate card modifiers: every field independently optional — include
    // whichever are actually filled in.
    const modifiers: NonNullable<UpdateTenantPatch["rateCardModifiers"]> = {};
    if (modifiersDraft.regionalMultiplier.trim()) {
      if (!isPositiveFiniteStr(modifiersDraft.regionalMultiplier)) {
        setSaveError("Regional multiplier must be a number greater than 0.");
        return;
      }
      modifiers.regionalMultiplier = Number(modifiersDraft.regionalMultiplier);
    }
    if (modifiersDraft.marginPct.trim()) {
      const n = Number(modifiersDraft.marginPct);
      if (!Number.isFinite(n) || n < 0) {
        setSaveError("Margin % must be a number 0 or greater.");
        return;
      }
      modifiers.marginPct = n;
    }
    if (modifiersDraft.rangeSpreadPct.trim()) {
      const n = Number(modifiersDraft.rangeSpreadPct);
      if (!Number.isFinite(n) || n < 0 || n > 1) {
        setSaveError("Range spread must be a number between 0 and 1.");
        return;
      }
      modifiers.rangeSpreadPct = n;
    }
    if (Object.keys(modifiers).length > 0) patch.rateCardModifiers = modifiers;

    // Deck material rates: rows with a rate filled in are included; a row
    // with a blank rate is just "not filled in yet," not an error — unlike
    // rateCard, this is an open-ended map, not a fixed required set.
    const filledMaterialRows = deckMaterialRows.filter(r => r.rate.trim() !== "");
    for (const row of filledMaterialRows) {
      if (!isPositiveFiniteStr(row.rate)) {
        setSaveError(`Deck rate for ${row.material} must be a number greater than 0.`);
        return;
      }
    }
    const deckRatesByMaterialUSD: Record<string, number> = {};
    for (const row of filledMaterialRows) deckRatesByMaterialUSD[row.material] = Number(row.rate);

    // Pricing extensions: only send the section at all if there's actual
    // content — a numeric field or a material row filled in, marginMode
    // changed from what loaded, or the tenant already had this object (so
    // an unwound-to-default marginMode still saves explicitly rather than
    // vanishing).
    const extensionsHasContent =
      extensionsDraft.perGangwayLFUSD.trim() !== "" ||
      extensionsDraft.perFloatUSD.trim() !== "" ||
      extensionsDraft.marginMode !== loadedMarginMode ||
      filledMaterialRows.length > 0 ||
      hadPricingExtensions;
    if (extensionsHasContent) {
      const extensions: NonNullable<UpdateTenantPatch["pricingExtensions"]> = {
        marginMode: extensionsDraft.marginMode,
      };
      if (extensionsDraft.perGangwayLFUSD.trim()) {
        if (!isPositiveFiniteStr(extensionsDraft.perGangwayLFUSD)) {
          setSaveError("Gangway $/LF must be a number greater than 0.");
          return;
        }
        extensions.perGangwayLFUSD = Number(extensionsDraft.perGangwayLFUSD);
      }
      if (extensionsDraft.perFloatUSD.trim()) {
        if (!isPositiveFiniteStr(extensionsDraft.perFloatUSD)) {
          setSaveError("Est. float $ must be a number greater than 0.");
          return;
        }
        extensions.perFloatUSD = Number(extensionsDraft.perFloatUSD);
      }
      if (filledMaterialRows.length > 0) {
        extensions.deckRatesByMaterialUSD = deckRatesByMaterialUSD;
      }
      patch.pricingExtensions = extensions;
    }

    // Estimate pricing (`pricing` / TenantPricing): unlike everything above,
    // the backend MERGES this key-by-key, so we only ever send the
    // top-level sub-keys that actually have content — no need to preserve
    // untouched fields via passthrough the way rateCard.stairFlatUSD does.
    const pricing: NonNullable<UpdateTenantPatch["pricing"]> = {};

    const filledDeckingRows = deckingRateRows.filter(r => r.rate.trim() !== "");
    for (const row of filledDeckingRows) {
      if (!isPositiveFiniteStr(row.rate)) {
        setSaveError(`Decking rate for ${row.material} must be a number greater than 0.`);
        return;
      }
    }
    if (filledDeckingRows.length > 0) {
      const deckingRatesPerSqft: Record<string, number> = {};
      for (const row of filledDeckingRows) deckingRatesPerSqft[row.material] = Number(row.rate);
      pricing.deckingRatesPerSqft = deckingRatesPerSqft;
    }

    const featureAddons: NonNullable<TenantPricing["featureAddons"]> = {};
    for (const { key, label } of FEATURE_ADDON_FIELDS) {
      const v = featureAddonsDraft[key];
      if (v.trim() === "") continue;
      if (!isPositiveFiniteStr(v)) {
        setSaveError(`${label} must be a number greater than 0.`);
        return;
      }
      featureAddons[key] = Number(v);
    }
    if (Object.keys(featureAddons).length > 0) pricing.featureAddons = featureAddons;

    if (laborApproachDraft) pricing.laborApproach = laborApproachDraft;
    if (laborValueDraft.trim() !== "") {
      const n = Number(laborValueDraft);
      if (!Number.isFinite(n) || n < 0) {
        setSaveError("Labor value must be a number 0 or greater.");
        return;
      }
      pricing.laborValue = n;
    }

    if (pricingMarginPctDraft.trim() !== "") {
      const n = Number(pricingMarginPctDraft);
      if (!Number.isFinite(n) || n < 0) {
        setSaveError("Estimate margin % must be a number 0 or greater.");
        return;
      }
      pricing.marginPct = n;
    }

    if (Object.keys(pricing).length > 0) patch.pricing = pricing;

    setIsSaving(true);
    try {
      const updated = await updateTenant(user.idToken, patch);
      setTenant(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      setSaveError(friendlyErrorMessage(err, "saving your rate card"));
    } finally {
      setIsSaving(false);
    }
  }

  if (!user) return null;

  const pageHeading = (
    <div className={ui.pageHeader}>
      <div>
        <h1>{sectionMeta.label}</h1>
        <p className={ui.pageHint}>{sectionMeta.hint}</p>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <SettingsLayout active={section}>
        <main id="main-content" className={ui.main}>
          {pageHeading}
          <p className={ui.loading}>Loading settings…</p>
        </main>
      </SettingsLayout>
    );
  }

  if (loadError && !tenant) {
    return (
      <SettingsLayout active={section}>
        {/* AUD-017's landmark fix + AUD-016's heading fix, applied to this
            failure branch too. */}
        <main id="main-content" className={ui.main}>
          <div className={ui.errorBox}>
            <h1>{sectionMeta.label}</h1>
            <StatusMessage variant="error" message={loadError} className={ui.error} />
            <div className={ui.actions}>
              {/* AUD-008: an in-place Retry, not just an exit. */}
              <button type="button" onClick={handleRetryLoad} className={ui.button}>
                Retry
              </button>
              <Link to="/leads" className={ui.buttonSecondary}>Back to leads</Link>
            </div>
          </div>
        </main>
      </SettingsLayout>
    );
  }

  if (!tenant) return null;

  // See this file's own header doc comment (and DEBT.md's round4/builder-
  // templates-portal entry) for why these are the only sections on this
  // page that read role at all.
  const isOwner = user.role === "owner";
  // Engineering rules: the CURRENT EFFECTIVE value, mirroring
  // applyTenantToForm's own pilingSpacingDraft population above — used by
  // the rep's read-only summary below (the owner's editable input reads
  // pilingSpacingDraft directly instead, since it's mid-edit state).
  const effectivePilingSpacingFt = tenant.engineeringRules?.pilingSpacingFt ?? PILING_SPACING_DEFAULT_FT;

  return (
    <SettingsLayout active={section}>
      {/* AUD-017: a single <main> ancestor for the whole form. */}
      <main id="main-content" className={ui.main}>
        <form className={styles.form} onSubmit={handleSave}>
          {pageHeading}

          {section === "engineering" && (
            /* Engineering rules (v1: pilingSpacingFt only) — round8/
               engineering-rules. Owner-only editable field + rep read-only
               summary. "Reset to default" is LOCAL DRAFT STATE ONLY — it
               does not fire its own save, per this page's "one page, one
               Save" rule (see header comment). */
            <section className={`${ui.card} ${styles.narrowCard}`}>
              <h2 className={ui.cardTitle}>Engineering rules</h2>
              <p className={ui.cardHint}>
                Structural conventions your DockPro instance designs with (v1: piling
                spacing only).
              </p>

              {isOwner ? (
                <>
                  <div className={styles.spacingRow}>
                    <label className={`${ui.field} ${styles.spacingField}`}>
                      <span>Piling spacing (6–12 ft)</span>
                      <input
                        type="number" min={PILING_SPACING_MIN_FT} max={PILING_SPACING_MAX_FT} step="0.5"
                        value={pilingSpacingDraft}
                        onChange={e => setPilingSpacingDraft(e.target.value)}
                      />
                    </label>
                    <p className={`${ui.subtle} ${styles.spacingNote}`}>
                      Default: {PILING_SPACING_DEFAULT_FT} ft.{" "}
                      <button
                        type="button"
                        className={`${ui.buttonGhost} ${ui.buttonSmall}`}
                        onClick={() => setPilingSpacingDraft(String(PILING_SPACING_DEFAULT_FT))}
                      >
                        Reset to default
                      </button>
                    </p>
                  </div>
                  <p className={`${ui.note} ${styles.noteSpaced}`}>
                    A piling goes at every corner, plus intermediate pilings so no gap along
                    an edge exceeds this. Changing piling spacing affects piling counts,
                    materials, and estimates on new designs. Existing saved designs keep the
                    spacing they were drawn with.
                  </p>
                </>
              ) : (
                // Rep view: read-only.
                <p className={ui.subtle}>
                  Piling spacing: <strong>{effectivePilingSpacingFt} ft</strong>
                  {tenant.engineeringRules?.pilingSpacingFt === undefined ? " (default)" : ""}
                  {" "}Only the account owner can change this.
                </p>
              )}
            </section>
          )}

          {section === "quoting" && (
            <section className={`${ui.card} ${styles.narrowCard}`}>
              <h2 className={ui.cardTitle}>Quoting</h2>
              <p className={ui.cardHint}>
                Turns on the instant, server-computed quote shown on a lead.
              </p>
              <div className={ui.fieldGrid}>
                <label className={`${ui.field} ${ui.checkboxField}`}>
                  <input
                    type="checkbox"
                    checked={quotingEnabled}
                    onChange={e => setQuotingEnabled(e.target.checked)}
                  />
                  <span>Quoting enabled</span>
                </label>
                <label className={ui.field}>
                  <span>Visibility</span>
                  <select
                    value={quoteVisibility}
                    onChange={e => setQuoteVisibility(e.target.value as QuoteVisibility | "")}
                  >
                    <option value="">— Not set —</option>
                    <option value="homeowner">Homeowner</option>
                    <option value="internal">Internal only</option>
                  </select>
                </label>
              </div>
              <label className={`${ui.field} ${ui.fieldSpaced}`}>
                <span>Disclaimer text</span>
                <textarea
                  value={disclaimerText}
                  onChange={e => setDisclaimerText(e.target.value)}
                  placeholder="Shown alongside the quote, e.g. site conditions / permitting caveats."
                />
              </label>
            </section>
          )}

          {section === "rate-card" && (
            <>
              <section className={ui.card}>
                <h2 className={ui.cardTitle}>Rate card</h2>
                <p className={ui.cardHint}>
                  Fill in every field to set or replace your rate card. Leave all blank to leave it unchanged.
                </p>
                <div className={ui.fieldGrid}>
                  {RATE_CARD_FIELDS.map(({ key, label }) => (
                    <label className={ui.field} key={key}>
                      <span>{label}</span>
                      <input
                        type="number" min="0" step="0.01"
                        value={rateCardDraft[key]}
                        onChange={e => setRateCardDraft(d => ({ ...d, [key]: e.target.value }))}
                      />
                    </label>
                  ))}
                </div>
              </section>

              <section className={ui.card}>
                <h2 className={ui.cardTitle}>Rate card modifiers</h2>
                <p className={ui.cardHint}>
                  Each is optional and independent — leave any blank to leave it unchanged.
                </p>
                <div className={ui.fieldGrid}>
                  <label className={ui.field}>
                    <span>Regional multiplier</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={modifiersDraft.regionalMultiplier}
                      onChange={e => setModifiersDraft(d => ({ ...d, regionalMultiplier: e.target.value }))}
                    />
                  </label>
                  <label className={ui.field}>
                    <span>Margin %</span>
                    <input
                      type="number" min="0" step="0.1"
                      value={modifiersDraft.marginPct}
                      onChange={e => setModifiersDraft(d => ({ ...d, marginPct: e.target.value }))}
                    />
                  </label>
                  <label className={ui.field}>
                    <span>Range spread (0–1)</span>
                    <input
                      type="number" min="0" max="1" step="0.01"
                      value={modifiersDraft.rangeSpreadPct}
                      onChange={e => setModifiersDraft(d => ({ ...d, rangeSpreadPct: e.target.value }))}
                    />
                  </label>
                </div>
              </section>

              <section className={ui.card}>
                <h2 className={ui.cardTitle}>Pricing extensions</h2>
                <p className={ui.cardHint}>
                  Additional pricing on top of the rate card above. Each is optional and independent.
                </p>
                <div className={ui.fieldGrid}>
                  <label className={ui.field}>
                    <span>Gangway ($/LF)</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={extensionsDraft.perGangwayLFUSD}
                      onChange={e => setExtensionsDraft(d => ({ ...d, perGangwayLFUSD: e.target.value }))}
                    />
                  </label>
                  <label className={ui.field}>
                    <span>Margin mode</span>
                    <select
                      value={extensionsDraft.marginMode}
                      onChange={e => setExtensionsDraft(d => ({ ...d, marginMode: e.target.value as MarginMode }))}
                    >
                      <option value="onCost">On cost (default)</option>
                      <option value="onPrice">On price</option>
                    </select>
                  </label>
                  <label className={ui.field}>
                    <span>Est. float ($)</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={extensionsDraft.perFloatUSD}
                      onChange={e => setExtensionsDraft(d => ({ ...d, perFloatUSD: e.target.value }))}
                    />
                  </label>
                </div>

                <h3 className={ui.subheading}>Deck rates by material</h3>
                <p className={ui.cardHint}>
                  Overrides the flat deck rate above for a specific material — priced as an
                  adjustment, not a replacement. Leave a row&rsquo;s rate blank to skip it.
                </p>
                {deckMaterialRows.length > 0 && (
                  <div className={styles.materialRows}>
                    {deckMaterialRows.map((row) => (
                      <div className={styles.materialRow} key={row.id}>
                        <select
                          aria-label="Material"
                          value={row.material}
                          onChange={e => setDeckMaterialRows(rows =>
                            rows.map(r => r.id === row.id ? { ...r, material: e.target.value } : r),
                          )}
                        >
                          {MATERIAL_OPTIONS.map(({ key, label }) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                        <input
                          aria-label="Rate ($/sqft)"
                          type="number" min="0" step="0.01"
                          placeholder="$/sqft"
                          value={row.rate}
                          onChange={e => setDeckMaterialRows(rows =>
                            rows.map(r => r.id === row.id ? { ...r, rate: e.target.value } : r),
                          )}
                        />
                        <button
                          type="button"
                          className={ui.iconButton}
                          onClick={() => setDeckMaterialRows(rows => rows.filter(r => r.id !== row.id))}
                          aria-label={`Remove ${row.material} rate`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className={`${ui.buttonGhost} ${ui.buttonSmall}`}
                  onClick={() => setDeckMaterialRows(rows => [
                    ...rows,
                    { id: nextRowId.current++, material: MATERIAL_OPTIONS[0].key, rate: "" },
                  ])}
                >
                  + Add material rate
                </button>
              </section>

              <section className={ui.card}>
                <h2 className={ui.cardTitle}>Estimate pricing</h2>
                <p className={ui.cardHint}>
                  Drives the estimated range shown on a lead and in the customer PDF (a
                  separate calculation from the rate card above). Each field is
                  optional and independent.
                </p>

                <h3 className={ui.subheading}>Decking rates ($/sqft)</h3>
                <p className={ui.cardHint}>
                  By material — use &ldquo;Default&rdquo; to set the fallback rate used
                  when a design&rsquo;s material has no specific rate below.
                </p>
                {deckingRateRows.length > 0 && (
                  <div className={styles.materialRows}>
                    {deckingRateRows.map((row) => (
                      <div className={styles.materialRow} key={row.id}>
                        <select
                          aria-label="Material"
                          value={row.material}
                          onChange={e => setDeckingRateRows(rows =>
                            rows.map(r => r.id === row.id ? { ...r, material: e.target.value } : r),
                          )}
                        >
                          {DECKING_RATE_MATERIAL_OPTIONS.map(({ key, label }) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                        <input
                          aria-label="Rate ($/sqft)"
                          type="number" min="0" step="0.01"
                          placeholder="$/sqft"
                          value={row.rate}
                          onChange={e => setDeckingRateRows(rows =>
                            rows.map(r => r.id === row.id ? { ...r, rate: e.target.value } : r),
                          )}
                        />
                        <button
                          type="button"
                          className={ui.iconButton}
                          onClick={() => setDeckingRateRows(rows => rows.filter(r => r.id !== row.id))}
                          aria-label={`Remove ${row.material} decking rate`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className={`${ui.buttonGhost} ${ui.buttonSmall}`}
                  onClick={() => setDeckingRateRows(rows => [
                    ...rows,
                    { id: nextRowId.current++, material: DECKING_RATE_MATERIAL_OPTIONS[0].key, rate: "" },
                  ])}
                >
                  + Add decking rate
                </button>

                <h3 className={ui.subheading}>Feature add-ons ($)</h3>
                <div className={ui.fieldGrid}>
                  {FEATURE_ADDON_FIELDS.map(({ key, label }) => (
                    <label className={ui.field} key={key}>
                      <span>{label}</span>
                      <input
                        type="number" min="0" step="0.01"
                        value={featureAddonsDraft[key]}
                        onChange={e => setFeatureAddonsDraft(d => ({ ...d, [key]: e.target.value }))}
                      />
                    </label>
                  ))}
                </div>

                <h3 className={ui.subheading}>Labor &amp; margin</h3>
                <div className={ui.fieldGrid}>
                  <label className={ui.field}>
                    <span>Labor approach</span>
                    <select
                      value={laborApproachDraft}
                      onChange={e => setLaborApproachDraft(e.target.value as LaborApproach | "")}
                    >
                      <option value="">— Not set —</option>
                      {LABOR_APPROACH_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </label>
                  <label className={ui.field}>
                    <span>Labor value</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={laborValueDraft}
                      onChange={e => setLaborValueDraft(e.target.value)}
                    />
                  </label>
                  <label className={ui.field}>
                    <span>Margin %</span>
                    <input
                      type="number" min="0" step="0.1"
                      value={pricingMarginPctDraft}
                      onChange={e => setPricingMarginPctDraft(e.target.value)}
                    />
                  </label>
                </div>
              </section>
            </>
          )}

          <div className={ui.saveBar}>
            <button type="submit" disabled={isSaving} className={ui.button}>
              {isSaving ? "Saving…" : "Save"}
            </button>
            {saved && (
              <StatusMessage variant="status" message="Saved." className={ui.savedInline} />
            )}
            {saveError && (
              <StatusMessage variant="error" message={saveError} className={ui.error} />
            )}
            {!saved && !saveError && (
              <span className={ui.subtle}>One Save covers every section on this rail.</span>
            )}
          </div>
        </form>
      </main>
    </SettingsLayout>
  );
}
